import { useEffect, useRef, useState, type DragEvent } from "react";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  LinearProgress,
  Stack,
  Step,
  StepLabel,
  Stepper,
  Switch,
  Typography,
} from "@mui/material";
import CloudUpload from "@mui/icons-material/CloudUpload";
import DescriptionOutlined from "@mui/icons-material/DescriptionOutlined";
import CheckCircle from "@mui/icons-material/CheckCircle";
import ErrorOutline from "@mui/icons-material/ErrorOutline";
import type { Processing } from "@ebook/contracts";
import { useTranslate } from "../i18n";
import { api } from "../lib/api-client";
import { multipartUpload, sha256 } from "../lib/asset-transfer-client";
import { fileSize, validatePdf } from "./PdfUploadDialog";
import { GlobalFileInput, GlobalInput } from "./GlobalInput";

type WizardStep = 0 | 1 | 2;

type AddEbookDialogProps = {
  open: boolean;
  onClose: () => void;
  onCreated: (ebookId: string) => void;
  pageLimit?: number;
};

export function AddEbookDialog({ open, onClose, onCreated, pageLimit = 3000 }: AddEbookDialogProps) {
  const __ = useTranslate();
  const inputRef = useRef<HTMLInputElement>(null);
  const [step, setStep] = useState<WizardStep>(0);
  const [title, setTitle] = useState("");
  const [file, setFile] = useState<File | null>(null);
  const [dragging, setDragging] = useState(false);
  const [fileError, setFileError] = useState("");
  const [creating, setCreating] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<number | null>(null);
  const [ebookId, setEbookId] = useState<string | null>(null);
  const [processing, setProcessing] = useState<Processing | null>(null);
  const [retrying, setRetrying] = useState(false);
  const [autoOcrScan, setAutoOcrScan] = useState(true);

  useEffect(() => {
    if (open) return;
    setStep(0);
    setTitle("");
    setFile(null);
    setDragging(false);
    setFileError("");
    setCreating(false);
    setUploadProgress(null);
    setEbookId(null);
    setProcessing(null);
    setRetrying(false);
    setAutoOcrScan(true);
  }, [open]);

  useEffect(() => {
    if (step !== 2 || !ebookId || processing?.status === "ready" || processing?.status === "failed") return;
    let cancelled = false;
    void api.processing(ebookId).then((value) => { if (!cancelled) setProcessing(value); });
    const timer = window.setInterval(() => {
      if (!navigator.onLine || document.hidden) return;
      void api.processing(ebookId).then((value) => { if (!cancelled) setProcessing(value); });
    }, 2_000);
    return () => { cancelled = true; window.clearInterval(timer); };
  }, [ebookId, processing?.status, step]);

  function chooseFile(next: File | undefined) {
    if (!next) return;
    const validationError = validatePdf(next);
    if (validationError) {
      setFile(null);
      setFileError(validationError);
      return;
    }
    setFile(next);
    setFileError("");
  }

  function drop(event: DragEvent<HTMLDivElement>) {
    event.preventDefault();
    setDragging(false);
    chooseFile(event.dataTransfer.files[0]);
  }

  async function createAndUpload() {
    if (!file || creating) return;
    setCreating(true);
    setFileError("");
    let createdId: string | null = null;
    try {
      const ebook = await api.createEbook(title.trim());
      createdId = ebook.id;
      const digest = await sha256(file);
      const started = await api.startUpload(ebook.id, {
        fileName: file.name,
        contentType: "application/pdf",
        size: file.size,
        sha256: digest,
        autoOcrScan,
      });
      setUploadProgress(0);
      const uploaded = await multipartUpload(file, started, setUploadProgress);
      await api.completeUpload(ebook.id, started.uploadId, uploaded.workerUploadId, uploaded.parts);
      setEbookId(ebook.id);
      setProcessing({ status: "queued", pageCount: 0, processedPages: 0, progress: 0, error: null });
      setStep(2);
    } catch (caught) {
      if (createdId) await api.deleteEbook(createdId).catch(() => undefined);
      setFileError(caught instanceof Error ? caught.message : __("สร้าง E-book ไม่สำเร็จ"));
    } finally {
      setUploadProgress(null);
      setCreating(false);
    }
  }

  async function createWithoutPdf() {
    if (creating) return;
    setCreating(true);
    setFileError("");
    try {
      const ebook = await api.createEbook(title.trim());
      onCreated(ebook.id);
    } catch (caught) {
      setFileError(caught instanceof Error ? caught.message : __("สร้าง E-book ไม่สำเร็จ"));
    } finally {
      setCreating(false);
    }
  }

  async function retryProcessing() {
    if (!ebookId) return;
    setRetrying(true);
    try {
      await api.retryProcessing(ebookId);
      setProcessing(await api.processing(ebookId));
    } finally {
      setRetrying(false);
    }
  }

  const uploading = uploadProgress !== null;
  const busy = creating || uploading;

  return <Dialog open={open} onClose={busy ? undefined : onClose} fullWidth maxWidth="sm">
    <DialogTitle fontWeight={800}>{__("Add Ebook")}</DialogTitle>
    <DialogContent>
      <Stepper activeStep={step} sx={{ mb: 3, mt: 1 }}>
        <Step><StepLabel>{__("ตั้งชื่อ")}</StepLabel></Step>
        <Step><StepLabel>{__("แนบไฟล์ PDF")}</StepLabel></Step>
        <Step><StepLabel>{__("ประมวลผล")}</StepLabel></Step>
      </Stepper>

      {step === 0 && (
        <GlobalInput
          fullWidth
          label={__("Title Ebook")}
          value={title}
          onChange={(event) => setTitle(event.target.value)}
          inputProps={{ maxLength: 180 }}
          autoFocus
        />
      )}

      {step === 1 && (
        <>
          <Typography variant="body2" color="text.secondary" mb={2}>
            {__("รองรับ PDF สูงสุด 250 MB และ {{count}} หน้า ตามแผนปัจจุบัน", { count: pageLimit })}
          </Typography>
          <FormControlLabel
            sx={{ mb: 2 }}
            control={
              <Switch
                checked={autoOcrScan}
                onChange={(event) => setAutoOcrScan(event.target.checked)}
                disabled={busy}
              />
            }
            label={__("AUTO OCR SCAN")}
          />
          {fileError && <Alert severity="error" sx={{ mb: 2 }}>{fileError}</Alert>}
          <Box
            onDragEnter={(event) => { event.preventDefault(); if (!busy) setDragging(true); }}
            onDragOver={(event) => event.preventDefault()}
            onDragLeave={(event) => { if (event.currentTarget === event.target) setDragging(false); }}
            onDrop={busy ? undefined : drop}
            sx={{
              minHeight: 220,
              p: 3,
              border: "2px dashed",
              borderColor: dragging ? "primary.main" : "divider",
              borderRadius: 3,
              display: "grid",
              placeItems: "center",
              textAlign: "center",
              bgcolor: dragging ? "action.hover" : "background.default",
              transition: "border-color .2s ease, background-color .2s ease",
            }}
          >
            {file ? (
              <Stack alignItems="center" spacing={1}>
                <DescriptionOutlined color="primary" sx={{ fontSize: 50 }} />
                <Typography fontWeight={700} sx={{ wordBreak: "break-word" }}>{file.name}</Typography>
                <Typography variant="body2" color="text.secondary">{fileSize(file.size)}</Typography>
                {!busy && <Button onClick={() => inputRef.current?.click()}>{__("เปลี่ยนไฟล์")}</Button>}
              </Stack>
            ) : (
              <Stack alignItems="center" spacing={1.5}>
                <CloudUpload color="primary" sx={{ fontSize: 54 }} />
                <Typography fontWeight={700}>{__("ลากไฟล์ PDF มาวางที่นี่")}</Typography>
                <Typography variant="body2" color="text.secondary">{__("หรือ")}</Typography>
                <Button variant="outlined" onClick={() => inputRef.current?.click()}>{__("เลือกไฟล์ PDF")}</Button>
              </Stack>
            )}
            <GlobalFileInput
              ref={inputRef}
              aria-label={__("เลือกไฟล์ PDF")}
              accept="application/pdf,.pdf"
              disabled={busy}
              onChange={(event) => { chooseFile(event.target.files?.[0]); event.target.value = ""; }}
            />
          </Box>
          {uploading && (
            <Box mt={2.5}>
              <LinearProgress variant="determinate" value={uploadProgress ?? 0} sx={{ height: 8, borderRadius: 8 }} />
              <Typography variant="body2" color="text.secondary" mt={1} textAlign="center">
                {__("กำลัง Upload {{progress}}%", { progress: uploadProgress ?? 0 })}
              </Typography>
            </Box>
          )}
        </>
      )}

      {step === 2 && processing && (
        <Stack alignItems="center" spacing={2} py={2}>
          {processing.status === "ready" ? (
            <>
              <CheckCircle color="success" sx={{ fontSize: 56 }} />
              <Typography fontWeight={700}>{__("E-book พร้อมใช้งานแล้ว")}</Typography>
            </>
          ) : processing.status === "failed" ? (
            <>
              <ErrorOutline color="error" sx={{ fontSize: 56 }} />
              <Typography fontWeight={700}>{processing.error ?? __("การประมวลผล PDF ไม่สำเร็จ")}</Typography>
              <Button variant="outlined" disabled={retrying} onClick={() => void retryProcessing()}>
                {retrying ? __("กำลังลองใหม่…") : __("ลองใหม่")}
              </Button>
            </>
          ) : (
            <Box width="100%">
              <Typography fontWeight={700} textAlign="center" mb={1}>{__("กำลังประมวลผล PDF")}</Typography>
              <LinearProgress
                variant="determinate"
                value={processing.progress}
                sx={{ height: 8, borderRadius: 8 }}
              />
              <Typography variant="body2" color="text.secondary" mt={1} textAlign="center">
                {__("{{processed}} / {{total}} หน้า ({{progress}}%)", {
                  processed: processing.processedPages,
                  total: processing.pageCount || "?",
                  progress: processing.progress,
                })}
              </Typography>
            </Box>
          )}
          <Typography variant="caption" color="text.secondary" textAlign="center">
            {__("ปิดหน้าต่างนี้ได้ การประมวลผลจะดำเนินต่อในพื้นหลัง")}
          </Typography>
        </Stack>
      )}
    </DialogContent>
    <DialogActions sx={{ px: 3, pb: 3 }}>
      {step === 0 && (
        <>
          <Button onClick={onClose}>{__("ยกเลิก")}</Button>
          <Button variant="contained" disabled={!title.trim()} onClick={() => setStep(1)}>{__("ถัดไป")}</Button>
        </>
      )}
      {step === 1 && (
        <>
          <Button disabled={busy} onClick={() => setStep(0)}>{__("ย้อนกลับ")}</Button>
          <Button disabled={busy} onClick={() => void createWithoutPdf()}>{__("Skip")}</Button>
          <Button
            variant="contained"
            startIcon={<CloudUpload />}
            disabled={!file || busy}
            onClick={() => void createAndUpload()}
          >
            {busy ? __("กำลังสร้างและอัปโหลด…") : __("สร้าง Ebook และอัปโหลด PDF")}
          </Button>
        </>
      )}
      {step === 2 && (
        <>
          <Button onClick={onClose}>{__("ปิด")}</Button>
          {processing?.status === "ready" && ebookId && (
            <Button variant="contained" onClick={() => onCreated(ebookId)}>{__("ไปที่ Ebook")}</Button>
          )}
        </>
      )}
    </DialogActions>
  </Dialog>;
}
